import React, { useEffect, useRef, forwardRef, RefObject } from 'react';
import { Loader } from '../Loader';

type RefProp<T> = RefObject<T | null> | null;

const WINDOW_ROOT_MARGIN_VIEWPORT_RATIO = 1;
const PARENT_ROOT_MARGIN = '0px 0px 150px 0px';

interface EndlessScrollProps {
  items: Readonly<{ length: number }>;
  loading: boolean;
  onLoadMore: () => void;
  isStopLoad: boolean;
  forwardedRef: RefProp<HTMLDivElement>;
  scrollContainer?: 'window' | 'parent';
}

const EndlessScroll: React.FC<
  EndlessScrollProps & {
    Component: React.ComponentType<{ forwardedRef: RefProp<HTMLDivElement> }>;
  }
> = ({
  onLoadMore,
  loading,
  isStopLoad,
  forwardedRef,
  scrollContainer = 'window',
  items,
  Component,
  ...props
}) => {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;
  const loadingRef = useRef(loading);
  loadingRef.current = loading;
  const isStopLoadRef = useRef(isStopLoad);
  isStopLoadRef.current = isStopLoad;
  const isIntersectingRef = useRef(false);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const root = scrollContainer === 'parent' ? forwardedRef?.current : null;
    console.log('[endlessScroll] setup effect', {
      scrollContainer,
      hasSentinel: !!sentinel,
      hasRoot: !!root
    });
    if (!sentinel || (scrollContainer === 'parent' && !root)) return;

    const rootMargin =
      scrollContainer === 'parent'
        ? PARENT_ROOT_MARGIN
        : `0px 0px ${Math.round(
            window.innerHeight * WINDOW_ROOT_MARGIN_VIEWPORT_RATIO
          )}px 0px`;

    console.log('[endlessScroll] creating observer', { rootMargin });

    const observer = new IntersectionObserver(
      ([entry]) => {
        isIntersectingRef.current = entry.isIntersecting;
        console.log('[endlessScroll] intersection', {
          isIntersecting: entry.isIntersecting,
          loading: loadingRef.current,
          isStopLoad: isStopLoadRef.current,
          boundingTop: entry.boundingClientRect?.top,
          rootBounds: entry.rootBounds
        });
        if (
          entry.isIntersecting &&
          !loadingRef.current &&
          !isStopLoadRef.current
        ) {
          console.log('[endlessScroll] calling onLoadMore (from observer)');
          onLoadMoreRef.current();
        }
      },
      { root, rootMargin }
    );

    observer.observe(sentinel);

    return () => {
      console.log('[endlessScroll] disconnecting observer');
      observer.disconnect();
    };
  }, [scrollContainer, forwardedRef]);

  useEffect(() => {
    console.log('[endlessScroll] loading/isStopLoad changed', {
      loading,
      isStopLoad,
      isIntersecting: isIntersectingRef.current
    });
    if (!loading && !isStopLoad && isIntersectingRef.current) {
      console.log('[endlessScroll] calling onLoadMore (from unblock effect)');
      onLoadMoreRef.current();
    }
  }, [loading, isStopLoad]);

  return (
    <>
      <Component {...props} forwardedRef={forwardedRef} />
      <div ref={sentinelRef} />
      <Loader loading={loading && !!items.length && !isStopLoad} endless />
    </>
  );
};

export const withEndlessScroll = <P extends object>(
  Component: React.ComponentType<
    P & { forwardedRef: RefObject<HTMLDivElement> }
  >
) => {
  const ForwardedComponent = forwardRef<
    HTMLDivElement,
    P & Omit<EndlessScrollProps, 'forwardedRef'>
  >((props, ref) => (
    <EndlessScroll
      {...props}
      forwardedRef={typeof ref !== 'function' ? ref : null}
      Component={
        Component as React.ComponentType<{
          forwardedRef: RefProp<HTMLDivElement>;
        }>
      }
    />
  ));

  const name = Component.displayName || Component.name;
  ForwardedComponent.displayName = `withEndlessScroll(${name})`;

  return ForwardedComponent;
};
